import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ReferralCard } from '../components/ReferralCard';
import { ShowWordsSwitch } from '../components/ShowWordsSwitch';
import { YourId } from '../components/YourId';
import { storeConfig } from '../config';
import {
  downloadBusinessStatement,
  fetchBusinessMe,
  fetchOrganisationDashboard,
  rememberBusinessToken,
  saveOrganisationSettings,
  setPersonOffice,
  type BusinessMe,
  type OrganisationDashboard as Dashboard,
} from '../lib/api';
import { confirmedSignedOut, useGentleRefresh } from '../lib/session-guard';
import { money } from '../lib/money';
import { useOzi } from '../state/ozi';
import { InboxLink } from '../components/Inbox';
import { readMessagesAloud } from '../lib/inbox-api';
import { organisationHelp, understandOrganisation } from '../voice/business-voice';
import { LiveClock } from '../components/LiveClock';
import { Reminders } from '../components/Reminders';
import { everydayOr } from '../voice/phrases';
import { listWords } from '../voice/staff-voice';
import { DAYS } from './WeeklyShop';

const field = 'w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3';

function day(when: string): string {
  return new Date(when).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** Every paid order as a spreadsheet, for the organisation's own accounts. */
export function statementCsv(data: Dashboard): string {
  const quote = (value: string): string => `"${value.replace(/"/g, '""')}"`;
  const rows = data.orders.map((order) =>
    [
      new Date(order.createdAt).toISOString().slice(0, 10),
      quote(order.person),
      quote(order.office),
      order.items,
      (order.paidPence / 100).toFixed(2),
      order.status,
      order.id,
    ].join(','),
  );
  const lines = ['Date,Person,Office,Items,Paid (GBP),Status,Order reference', ...rows];
  // What the organisation pays a month for the people it looks after (ruling 58).
  if (data.plan) {
    lines.push(
      '',
      `Monthly plan,${quote(data.plan.words)},,,${(data.plan.monthlyPence / 100).toFixed(2)},,`,
    );
  }
  return lines.join('\n');
}

/** What Ozi says about the money, in one go. */
export function spendingWords(data: Dashboard): string {
  const t = data.totals;
  return (
    `This month, ${money(t.spentThisMonthPence)} on ${t.deliveriesThisMonth} deliver${t.deliveriesThisMonth === 1 ? 'y' : 'ies'}. ` +
    `Last month, ${money(t.spentLastMonthPence)}. Altogether, ${money(t.spentAllTimePence)}.` +
    (data.plan ? ` Your plan is ${money(data.plan.monthlyPence)} a month.` : '')
  );
}

export function savedWords(data: Dashboard): string {
  const saved = data.totals.savedThisMonthPence;
  if (saved === null) {
    return 'To work out what you save, tell me what one of your own staff going to the shops costs you, in the box on the screen called "What a staff trip to the shops costs you".';
  }
  return `About ${money(saved)} saved this month: ${data.totals.deliveriesThisMonth} deliveries with delivery at ${data.deliveryWords}, instead of ${money(data.organisation.staffTripCostPence ?? 0)} for a staff trip.`;
}

export function budgetWords(data: Dashboard): string {
  const left = data.totals.budgetLeftPence;
  if (left === null) return 'You have not set a monthly budget. You can, on the screen.';
  return left >= 0
    ? `${money(left)} left of your ${money(data.organisation.monthlyBudgetPence ?? 0)} budget this month.`
    : `This month is ${money(-left)} over your ${money(data.organisation.monthlyBudgetPence ?? 0)} budget.`;
}

/**
 * An organisation's own area (7 October 2026): the people it supports who chose to link to it,
 * who looks after each, every order with who it was for and what it cost, spending by office,
 * budget, savings, what is coming up, and a statement to download. Ozi reads all of it aloud.
 */
export function OrganisationDashboard(): JSX.Element {
  const navigate = useNavigate();
  const ozi = useOzi();
  const [me, setMe] = useState<BusinessMe | null>(null);
  const [data, setData] = useState<Dashboard | null>(null);
  const [news, setNews] = useState('');
  const [problem, setProblem] = useState('');
  const latest = useRef({ ozi, data, navigate });
  const phraseTurn = useRef(0);
  latest.current = { ozi, data, navigate };

  const load = useCallback(
    () =>
      fetchOrganisationDashboard()
        .then((result) => {
          setData(result);
          return result;
        })
        .catch(async (failure: unknown) => {
          // Only the server's own word that the sign-in itself has ended, said twice ten seconds
          // apart, sends anyone back to sign in (ruling 88). Anything else is said plainly.
          if (!(await confirmedSignedOut(failure, fetchBusinessMe))) {
            setProblem(failure instanceof Error ? failure.message : 'Something went wrong.');
            return null;
          }
          rememberBusinessToken(null);
          navigate('/business');
          return null;
        }),
    [navigate],
  );

  useEffect(() => {
    const say = (words: string): void => void latest.current.ozi.say(words);
    const handle = (text: string): boolean => {
      const command = understandOrganisation(text);
      const current = latest.current.data;
      if (!command) {
        everydayOr(
          text,
          phraseTurn,
          { kind: 'business' },
          say,
          `Sorry, I didn't catch that. ${organisationHelp()}`,
          (work) => latest.current.ozi.think(work),
        );
        return true;
      }
      if (command.kind === 'help') return (say(organisationHelp()), true);
      if (command.kind === 'messages') {
        void readMessagesAloud('business', latest.current.ozi, storeConfig.productName);
        return true;
      }
      if (command.kind === 'sign-out') {
        rememberBusinessToken(null);
        say('Signed out. Goodbye.');
        latest.current.navigate('/business');
        return true;
      }
      if (!current) return true;
      switch (command.kind) {
        case 'summary':
        case 'spent':
          say(spendingWords(current));
          break;
        case 'saved':
          say(savedWords(current));
          break;
        case 'budget':
          say(budgetWords(current));
          break;
        case 'orders': {
          const recent = current.orders
            .slice(0, 5)
            .map(
              (order) =>
                `${day(order.createdAt)}, for ${order.person}${order.office ? `, ${order.office}` : ''}, ${money(order.paidPence)}`,
            );
          say(
            recent.length === 0
              ? 'No orders yet.'
              : `The latest ${recent.length === 1 ? 'order' : `${recent.length} orders`}: ${recent.join('. ')}.`,
          );
          break;
        }
        case 'people':
          say(
            current.people.length === 0
              ? `Nobody has linked yet. Give people your code, ${(current.organisation.joinCode ?? '').split('').join(' ')}, to type in their Settings.`
              : `You support ${current.people.length}: ${listWords(current.people.map((person) => (person.office ? `${person.name}, ${person.office}` : person.name)))}.`,
          );
          break;
        case 'offices':
          say(
            current.byOffice.length === 0
              ? 'Nothing spent this month yet.'
              : `This month: ${listWords(current.byOffice.map((row) => `${row.office}, ${money(row.pence)}`))}.`,
          );
          break;
        case 'upcoming':
          say(
            current.upcoming.length === 0
              ? 'No weekly shops are booked.'
              : `Weekly shops booked: ${listWords(current.upcoming.map((row) => `${row.person} every ${DAYS[row.dayOfWeek - 1]}, about ${money(row.estimatePence)}`))}. About ${money(current.totals.upcomingWeeklyPence)} a week altogether.`,
          );
          break;
      }
      return true;
    };
    latest.current.ozi.setPageCommands(handle);
    void Promise.all([fetchBusinessMe().catch(() => null), load()]).then(([who, result]) => {
      if (who) setMe(who);
      if (!result) return;
      say(
        `Welcome${who ? `, ${who.name}` : ''}, to ${result.organisation.name}'s ${storeConfig.productName} area. ` +
          `${spendingWords(result)} ${result.totals.budgetLeftPence !== null ? budgetWords(result) : ''} Say "help" to hear what I can tell you.`,
      );
    });
    return () => latest.current.ozi.setPageCommands(null);
  }, [load]);

  const saveSettings = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const pence = (name: string): number | null => {
      const raw = String(values.get(name) ?? '').replace(/[£,\s]/g, '');
      return raw === '' ? null : Math.round(Number(raw) * 100);
    };
    void saveOrganisationSettings({
      monthlyBudgetPence: pence('budget'),
      staffTripCostPence: pence('trip'),
    }).then((result) => {
      setNews(result.message);
      void load();
    });
  };

  const download = (): void => {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([statementCsv(data)], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data.organisation.name} statement.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setNews('Your statement is downloading.');
  };

  // Coming back to the screen, and once an hour while open, asks "me" again, so a session in use
  // is renewed before it ends (ruling 88). Never signs anybody out.
  const keepFresh = useCallback(async () => {
    await fetchBusinessMe().catch(() => undefined);
  }, []);
  useGentleRefresh(keepFresh);

  if (!data) {
    return problem ? (
      <div className="space-y-4">
        <p role="alert" className="m-0">
          {problem}
        </p>
        <button
          type="button"
          className="control bg-paper text-ink"
          onClick={() => {
            setProblem('');
            void load();
          }}
        >
          Try again
        </button>
      </div>
    ) : (
      <p role="status" className="m-0">
        One moment.
      </p>
    );
  }
  const t = data.totals;
  const tiles: Array<[string, string]> = [
    ['Spent this month', money(t.spentThisMonthPence)],
    ['Deliveries this month', String(t.deliveriesThisMonth)],
    ['Spent last month', money(t.spentLastMonthPence)],
    ['Spent altogether', money(t.spentAllTimePence)],
    ['Left in the budget', t.budgetLeftPence === null ? 'No budget set' : money(t.budgetLeftPence)],
    [
      'Saved this month',
      t.savedThisMonthPence === null ? 'Add your staff trip cost' : money(t.savedThisMonthPence),
    ],
    ['Booked each week', money(t.upcomingWeeklyPence)],
  ];

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-display font-bold m-0">{data.organisation.name}</h1>
        <LiveClock />
      </div>
      <nav aria-label="Your messages">
        <InboxLink as="business" to="/organisation/messages" />
      </nav>
      {/* The organisation's speakable ID (ruling 97). */}
      <YourId words="Organisation ID" id={data.organisation.publicId} />
      <p className="m-0">
        <Link
          to="/organisation/pay-by-bank"
          className="control border-paper bg-paper/10 text-paper"
        >
          Pay us by bank: our details and your reference
        </Link>
      </p>
      {/* Reminders for everyone (ruling 93), on this dashboard too. */}
      <Reminders as="business" />
      <details className="max-w-xl">
        <summary className="control px-0 text-paper underline">The screen</summary>
        <ShowWordsSwitch />
      </details>
      {me && (
        <p className="m-0">
          Signed in as {me.name}
          {me.office ? `, ${me.office}` : ''}.
        </p>
      )}
      <p role="status" className="m-0 min-h-control">
        {news}
      </p>

      <section aria-labelledby="money-heading" className="space-y-3">
        <h2 id="money-heading" className="text-lead font-bold m-0">
          At a glance
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 m-0">
          {tiles.map(([label, value]) => (
            <div key={label} className="border-2 border-paper rounded-2xl p-4">
              <dt className="m-0">{label}</dt>
              <dd className="m-0 text-lead font-bold">{value}</dd>
            </div>
          ))}
        </dl>
        {data.plan && (
          <p className="m-0">
            Your monthly plan: {money(data.plan.monthlyPence)} a month. {data.plan.words}
          </p>
        )}
        <button
          type="button"
          onClick={() =>
            void downloadBusinessStatement('organisation', data.organisation.name)
              .then(() => setNews('Your statement is downloading, as a PDF.'))
              .catch(() => setNews('The statement could not be made just now.'))
          }
          className="control bg-highlight text-on-accent"
        >
          Download a statement (PDF)
        </button>{' '}
        <button
          type="button"
          onClick={download}
          className="control border-paper bg-paper/10 text-paper"
        >
          Download it as a spreadsheet (CSV)
        </button>
      </section>

      <section aria-labelledby="link-heading" className="space-y-2">
        <h2 id="link-heading" className="text-lead font-bold m-0">
          Linking the people you support
        </h2>
        <p className="m-0">
          Each person, or someone with legal authority to act for them, types your code in their{' '}
          {storeConfig.productName} Settings. They are told first exactly what you will see: their
          name, the office label you give them, their booked weekly shops, and the orders they place
          from then on with what they cost. They can stop it at any time.
        </p>
        {data.sharePath && (
          <>
            <p className="m-0">Or share this link with your staff and the people you support:</p>
            <p className="m-0 break-all">{`${window.location.origin}${data.sharePath}`}</p>
            <button
              type="button"
              onClick={() => {
                const url = `${window.location.origin}${data.sharePath}`;
                const done = (words: string): void => setNews(words);
                if (navigator.share) {
                  void navigator
                    .share({ title: data.organisation.name, url })
                    .then(() => done('Shared.'))
                    .catch(() => undefined);
                } else {
                  void navigator.clipboard
                    ?.writeText(url)
                    .then(() => done('The link is copied. Paste it into an email or a message.'));
                }
              }}
              className="control border-paper bg-paper/10 text-paper"
            >
              Share our link
            </button>
            {/* Only the count, each person once: there is no target (30 October 2026). */}
            <p className="m-0 font-bold">
              {data.referrals} {data.referrals === 1 ? 'person' : 'people'} linked or joined through
              your link
            </p>
          </>
        )}
        <p className="m-0 text-lead font-bold">
          Your code:{' '}
          <span aria-label={(data.organisation.joinCode ?? '').split('').join(' ')}>
            {data.organisation.joinCode}
          </span>
        </p>
      </section>

      {/* Their own referral link, and Ozi Ambassadors (Anthony, 30 October 2026). */}
      <ReferralCard as="business" onNews={setNews} />

      <section aria-labelledby="people-heading" className="space-y-3">
        <h2 id="people-heading" className="text-lead font-bold m-0">
          The people you support
        </h2>
        {data.people.length === 0 && <p className="m-0">Nobody has linked yet.</p>}
        <ul className="list-none m-0 p-0 space-y-2">
          {data.people.map((person) => (
            <li key={person.id} className="border-2 border-paper/40 rounded-2xl p-3">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const office = String(new FormData(event.currentTarget).get('office') ?? '');
                  void setPersonOffice(person.id, office).then((result) => {
                    setNews(result.message);
                    void load();
                  });
                }}
                className="flex flex-wrap gap-2 items-end"
              >
                <label className="block flex-1">
                  <span className="block font-bold">{person.name}</span>
                  {person.agreed === false && (
                    <span className="block">
                      Linked before we asked everyone to agree to the new words, so you see none of
                      their orders or weekly shops until they link again with your code.
                    </span>
                  )}
                  Office or team that looks after them
                  <input name="office" defaultValue={person.office} className={field} />
                </label>
                <button type="submit" className="control border-paper bg-paper/10 text-paper">
                  Save<span className="visually-hidden"> {person.name}'s office</span>
                </button>
              </form>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="orders-heading" className="space-y-3">
        <h2 id="orders-heading" className="text-lead font-bold m-0">
          Orders
        </h2>
        {data.orders.length === 0 && <p className="m-0">No orders yet.</p>}
        <ul className="list-none m-0 p-0 space-y-2">
          {data.orders.map((order) => (
            <li key={order.id} className="border-2 border-paper/40 rounded-2xl p-3">
              {day(order.createdAt)}: for {order.person}
              {order.office ? ` (${order.office})` : ''}, {order.items} item
              {order.items === 1 ? '' : 's'}, {money(order.paidPence)}.
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="offices-heading" className="space-y-2">
        <h2 id="offices-heading" className="text-lead font-bold m-0">
          Spending by office this month
        </h2>
        {data.byOffice.length === 0 ? (
          <p className="m-0">Nothing yet this month.</p>
        ) : (
          <ul className="m-0 ps-6">
            {data.byOffice.map((row) => (
              <li key={row.office}>
                {row.office}: {money(row.pence)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="upcoming-heading" className="space-y-2">
        <h2 id="upcoming-heading" className="text-lead font-bold m-0">
          Coming up: weekly shops
        </h2>
        {data.upcoming.length === 0 ? (
          <p className="m-0">None booked.</p>
        ) : (
          <ul className="m-0 ps-6">
            {data.upcoming.map((row, index) => (
              <li key={`${row.person}-${index}`}>
                {row.person}, every {DAYS[row.dayOfWeek - 1]}, about {money(row.estimatePence)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <form onSubmit={saveSettings} className="space-y-3" aria-labelledby="settings-heading">
        <h2 id="settings-heading" className="text-lead font-bold m-0">
          Budget and savings
        </h2>
        <label htmlFor="budget" className="block font-bold">
          Monthly budget, in pounds (optional)
        </label>
        <input
          id="budget"
          name="budget"
          inputMode="decimal"
          defaultValue={
            data.organisation.monthlyBudgetPence === null
              ? ''
              : (data.organisation.monthlyBudgetPence / 100).toFixed(2)
          }
          className={field}
        />
        <label htmlFor="trip" className="block font-bold">
          What a staff trip to the shops costs you, in pounds (optional)
        </label>
        <p className="m-0 text-paper/90">
          Staff time and travel for one trip. It is how we work out what you save.
        </p>
        <input
          id="trip"
          name="trip"
          inputMode="decimal"
          defaultValue={
            data.organisation.staffTripCostPence === null
              ? ''
              : (data.organisation.staffTripCostPence / 100).toFixed(2)
          }
          className={field}
        />
        <button type="submit" className="control border-paper bg-paper/10 text-paper">
          Save
        </button>
      </form>

      <button
        type="button"
        onClick={() => {
          rememberBusinessToken(null);
          navigate('/business');
        }}
        className="control bg-paper/10 text-paper underline"
      >
        Sign out
      </button>
    </div>
  );
}
